import { useRole } from "@/data/RoleContext";
import { useBranding } from "@/data/BrandingContext";
import {
  ShieldCheck, Crown, Settings2, Clipboard, HardHat, Truck,
  TrendingUp, Warehouse, Monitor,
} from "lucide-react";
import type { UserRole } from "@/data/RoleContext";

const roleConfig: Record<UserRole, { icon: React.ElementType; focus: string; color: string; bg: string }> = {
  super_admin: { icon: ShieldCheck, focus: "Full platform visibility · All modules", color: "text-primary", bg: "bg-primary/10 border-primary/20" },
  owner: { icon: Crown, focus: "KPIs · Pipeline health · Region performance · Contractor scorecards", color: "text-primary", bg: "bg-primary/10 border-primary/20" },
  ops_manager: { icon: Settings2, focus: "Dispatch · Scheduling · Blockers · Crew utilization", color: "text-info", bg: "bg-info/10 border-info/20" },
  admin: { icon: Clipboard, focus: "Permits · Ordering · Scheduling · Documentation · Closeout", color: "text-accent", bg: "bg-accent/10 border-accent/20" },
  warehouse_manager: { icon: Warehouse, focus: "Intake · Staging · Material readiness · Inventory", color: "text-warning", bg: "bg-warning/10 border-warning/20" },
  installer: { icon: HardHat, focus: "Active installs · QA checklists · Issue reporting", color: "text-info", bg: "bg-info/10 border-info/20" },
  subcontractor: { icon: Truck, focus: "Opportunities · Active jobs · QA · Compliance", color: "text-accent", bg: "bg-accent/10 border-accent/20" },
  sales: { icon: TrendingUp, focus: "Pipeline · Lead performance · Customer issues · Revenue", color: "text-success", bg: "bg-success/10 border-success/20" },
  cfo: { icon: Crown, focus: "Cash flow · AR · Margin protection · Financial risk", color: "text-primary", bg: "bg-primary/10 border-primary/20" },
  finance_manager: { icon: Crown, focus: "Receivables · Credits · Deposit compliance · Job financials", color: "text-primary", bg: "bg-primary/10 border-primary/20" },
  bookkeeper: { icon: Clipboard, focus: "Payment tracking · Invoicing · Credit records", color: "text-accent", bg: "bg-accent/10 border-accent/20" },
  head_of_installation: { icon: HardHat, focus: "Job readiness · Crew scheduling · Material flow · Install execution", color: "text-info", bg: "bg-info/10 border-info/20" },
  procurement_manager: { icon: Truck, focus: "Purchasing · Vendor management · Stock levels · Supply chain", color: "text-warning", bg: "bg-warning/10 border-warning/20" },
  inventory_manager: { icon: Warehouse, focus: "Stock tracking · Allocation · Reorder management", color: "text-warning", bg: "bg-warning/10 border-warning/20" },
  sales_manager: { icon: TrendingUp, focus: "Rep performance · Scorecards · Appointment analytics · Sales oversight", color: "text-success", bg: "bg-success/10 border-success/20" },
  call_center: { icon: Clipboard, focus: "Inbound leads · Conversations · Follow-ups · Appointment scheduling", color: "text-info", bg: "bg-info/10 border-info/20" },
  it_staff: { icon: Monitor, focus: "System configuration · Settings · Technical administration", color: "text-accent", bg: "bg-accent/10 border-accent/20" },
};

export function RoleContextBanner() {
  const { roleInfo, role } = useRole();
  const { branding } = useBranding();
  const config = roleConfig[role];
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border p-3 flex items-center gap-3 ${config.bg}`}>
      <div className={`h-9 w-9 rounded-lg bg-background/80 flex items-center justify-center shrink-0`}>
        <Icon className={`h-4.5 w-4.5 ${config.color}`} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold tracking-tight">{roleInfo.title}</h2>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-background/60 ${config.color}`}>
            {roleInfo.label}
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">{config.focus}</p>
      </div>
    </div>
  );
}
